/**
 * useApplicationLogs Hook
 *
 * React Query hook for fetching CloudWatch logs for an application.
 */

import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { applicationService } from '../../../api/application.service';
import type {
  ApplicationLogsQuery,
  ApplicationLogsResponse,
} from '../../../types/application.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * Hook for fetching application logs
 */
export const useApplicationLogs = (
  tenantId: string,
  applicationId: string,
  query?: ApplicationLogsQuery
): UseQueryResult<ApplicationLogsResponse, Error> => {
  return useQuery<ApplicationLogsResponse, Error>({
    queryKey: ['applications', tenantId, applicationId, 'logs', query],
    queryFn: async () => {
      console.log('[useApplicationLogs] Fetching logs for application:', applicationId);
      return await applicationService.queryLogs(tenantId, applicationId, query);
    },
    enabled: !!tenantId && !!applicationId,
    staleTime: 0, // Always fetch fresh data when query changes
    onError: (error) => {
      console.error('[useApplicationLogs] Failed to fetch logs:', getErrorMessage(error));
    },
  });
};
